import { memo } from "react";

import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import isEqual from "lodash/isEqual";

import { FeedbackType } from "../../../../../API";
import { checkIfEventPast7Days } from "../../../../../common/utils/checkIfEventPast7Days";
import { IRecentEventsTableRow } from "../common/events.models";

interface FeedbackThumbsProps {
  params: IRecentEventsTableRow;
  onThumbUpClick: () => void;
  onThumbsDownClick: () => void;
}

// TODO: Remove this component when the new Recent Activities page is released.
const FeedbackThumbsComponent = ({
  params,
  onThumbUpClick,
  onThumbsDownClick,
}: FeedbackThumbsProps) => {
  const time = params?.time;
  const feedback = params?.feedback;
  const isDisabled = checkIfEventPast7Days(time);
  const tooltipText =
    "Feedback can no longer be updated for events older than 7 days";

  const confirmNegativeFeedback = (): void => {
    if (feedback === FeedbackType.NEGATIVE) {
      return;
    }

    onThumbsDownClick();
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5em",
        cursor: "pointer",
      }}
    >
      {isDisabled ? (
        <Tooltip
          title={<Typography variant="body2Regular">{tooltipText}</Typography>}
          placement="top-start"
          arrow
        >
          <IconButton aria-label="thumb-up" className="thumb-up">
            {feedback === FeedbackType.POSITIVE && (
              <ThumbUpIcon color={isDisabled ? "disabled" : "primary"} />
            )}
            {(!feedback || feedback === FeedbackType.NEGATIVE) && (
              <ThumbUpOutlinedIcon color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <IconButton
          aria-label="thumb-up"
          className="thumb-up"
          onClick={onThumbUpClick}
        >
          {feedback === FeedbackType.POSITIVE && (
            <ThumbUpIcon color={isDisabled ? "disabled" : "primary"} />
          )}
          {feedback === FeedbackType.NEGATIVE && (
            <ThumbUpOutlinedIcon color="primary" />
          )}
          {!feedback && <ThumbUpOutlinedIcon color="primary" />}
        </IconButton>
      )}

      {isDisabled ? (
        <Tooltip
          title={<Typography variant="body2Regular">{tooltipText}</Typography>}
          placement="top-start"
          arrow
        >
          <IconButton aria-label="thumb-down" className="thumb-down">
            {feedback === FeedbackType.NEGATIVE && (
              <ThumbDownIcon color={isDisabled ? "disabled" : "error"} />
            )}
            {(!feedback || feedback === FeedbackType.POSITIVE) && (
              <ThumbDownOutlinedIcon color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <IconButton
          aria-label="thumb-down"
          className="thumb-down"
          onClick={confirmNegativeFeedback}
        >
          {feedback === FeedbackType.POSITIVE && (
            <ThumbDownOutlinedIcon color="error" />
          )}
          {feedback === FeedbackType.NEGATIVE && (
            <ThumbDownIcon color={isDisabled ? "disabled" : "error"} />
          )}
          {!feedback && <ThumbDownOutlinedIcon color="error" />}
        </IconButton>
      )}
    </Box>
  );
};

const FeedbackThumbs = memo(FeedbackThumbsComponent, (prevProps, nextProps) => {
  const isEqualFeedback =
    prevProps.params?.feedback === nextProps.params?.feedback;

  const isEqualMitigatedRisk = isEqual(
    prevProps.params?.mitigatedRisk,
    nextProps.params?.mitigatedRisk
  );

  return isEqualFeedback && isEqualMitigatedRisk;
});

export default FeedbackThumbs;
